import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSession, useTrainerSessions, useUpdateSession } from "../../lib/queries";
import { ErrorBox, Loading } from "../../components/Loading";
import { useAuth } from "../../AuthContext";
import { closeApp, hapticNotification, setBackButton } from "../../lib/tg";
import { useToast } from "../../components/Toast";

export default function EditSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const sessionQ = useSession(id);
  const sessionsQ = useTrainerSessions(user?.trainer_id ?? undefined);
  const update = useUpdateSession();

  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [initialized, setInitialized] = useState(false);

  useEffect(() => setBackButton(() => navigate(`/t/session/${id}`)), [navigate, id]);

  useEffect(() => {
    if (sessionQ.data && !initialized) {
      setLocation(sessionQ.data.location_text ?? "");
      setNotes(sessionQ.data.notes ?? "");
      setInitialized(true);
    }
  }, [sessionQ.data, initialized]);

  const locationHistory = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const s of sessionsQ.data ?? []) {
      if (s.location_text && !seen.has(s.location_text)) {
        seen.add(s.location_text); out.push(s.location_text);
        if (out.length >= 5) break;
      }
    }
    return out;
  }, [sessionsQ.data]);

  if (sessionQ.isLoading) return <Loading />;
  if (sessionQ.error || !sessionQ.data) return <ErrorBox message="Не знайдено" />;
  const session = sessionQ.data;

  const isChanged = location.trim() !== (session.location_text ?? "") || notes.trim() !== (session.notes ?? "");

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-2xl font-bold">Редагувати</h1>
      <div className="text-tg-hint text-sm">
        {session.activity_type.emoji} {session.activity_type.name}
      </div>

      <section className="card space-y-3">
        <div>
          <div className="text-sm text-tg-hint mb-1">Локація</div>
          <input
            className="w-full bg-tg-bg border border-tg-secondary rounded-xl px-3 py-2"
            placeholder="наприклад, ВДНГ біля турніків"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            list="loc-history"
          />
          <datalist id="loc-history">
            {locationHistory.map((loc) => <option key={loc} value={loc} />)}
          </datalist>
          {locationHistory.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {locationHistory.map((loc) => (
                <button key={loc} className="chip text-sm" onClick={() => setLocation(loc)}>{loc}</button>
              ))}
            </div>
          )}
          <p className="text-xs text-tg-hint mt-2">
            Якщо локація зміниться — бот авто-DM-ить усім записаним батькам.
          </p>
        </div>

        <div>
          <div className="text-sm text-tg-hint mb-1">Нотатки</div>
          <textarea
            className="w-full bg-tg-bg border border-tg-secondary rounded-xl px-3 py-2"
            rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </section>

      <button
        className="btn-primary w-full"
        disabled={!isChanged || update.isPending}
        onClick={async () => {
          try {
            await update.mutateAsync({
              id: session.id,
              location_text: location.trim() || null,
              notes: notes.trim() || null,
            });
            hapticNotification("success");
            toast.success("Збережено");
            closeApp();
          } catch (e) {
            hapticNotification("error");
            toast.error((e as Error).message);
          }
        }}
      >Зберегти</button>
    </div>
  );
}
